import * as React from 'react';
import { styled } from '@mui/system';
import colors from '../../../../../../config/theme/colors';
import { Container, Item } from 'Components/Grid';
import ActionButton from 'Components/ActionButton';
import { Dialog, DialogActions, DialogContent, DialogTitle, Paper, PaperProps } from '@mui/material';
import { dialogActions, dialogContent, dialogTitle } from '../../../../../../config/theme/componentTheme';
import CDRForm from 'Routes/Cognition/Document/Form/CDR';
import { actions, TCreationPromiseType } from 'Store/documents/actions';
import { actions as actionsForm } from 'Store/form';
import { useAppDispatch } from 'Store/index';
import { IFormContext } from 'Containers/FormContextHandler';
import { sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { getCdrClass } from 'Routes/Cognition/utils';

const StyledArea = styled('div', {
  shouldForwardProp: prop => prop !== 'filled',
})(({ filled }: { filled?: boolean }) => ({
  padding: '1rem 2rem',
  border: `0.2rem solid ${filled ? colors.primary : colors.gray}`,
  borderRadius: '0.6rem',
}));

const StyledButton = styled('a')({
  fontSize: '1.6rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: colors.primary,
  textTransform: 'uppercase',
});

const DialogPaper = (props: PaperProps): React.JSX.Element => <Paper square={true} {...props} />;

const CDRDialog = ({ documents = [], formData, editing, view, fm }: IOwnProps): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [editingData, setEditingData] = React.useState<Omit<ICDR, keyof IControlProps>>({});
  const dispatch = useAppDispatch();

  const isViewing = !editing && !!view?.viewing;

  const latestCdr: ICDR | undefined = documents
    .sort((a, b) => b._cdate - a._cdate)
    .sort((a, b) => sortPartialDate(b.date, a.date))
    .find(d => d._type === 'cdr' && sortPartialDate(d.date, formData.document.date) <= 0);

  // Create new cdr document
  const addNewCdr = (): void => {
    const fetchID = async (): Promise<TCreationPromiseType> => actions.createDocument('cdr', true)(dispatch);
    fetchID()
      .then((id: TCreationPromiseType) => {
        if (typeof id === 'object' && id._id) {
          actions.closeCommit(
            { id: id._id as string, name: 'cdr' },
            editingData as TAnyObject & IControlProps,
          )(dispatch);
          dispatch(actionsForm.unsetEditing(id._id as string));
        }
      })
      .catch(() => {
        return;
      });
  };

  const dialogContentRef = React.useRef(null);

  return (
    <React.Fragment>
      <StyledArea>
        <Container justifyContent="flex-end" alignItems="center">
          <Item xs={8}>
            {!latestCdr ? (
              fm('diagnosis.alzheimer.cdrNotFilled')
            ) : (
              <Container>
                <Item style={{ fontWeight: 600 }}>{getCdrClass(latestCdr) ?? '-'}</Item>
                <Item style={{ padding: '0 1rem' }}>{` (${formatPartialDate(latestCdr.date)})`}</Item>
              </Container>
            )}
          </Item>
          <Item xs={4}>
            <Container justifyContent="flex-end">
              <Item>
                {true && (
                  <ActionButton
                    text="cognition.cdr.newCdrShort"
                    onClick={() => setDialogOpen(true)}
                    width={13}
                    height={3}
                    fontSize={16}
                    disabled={isViewing}
                  />
                )}
              </Item>
            </Container>
          </Item>
        </Container>
      </StyledArea>
      <Dialog open={dialogOpen} maxWidth="lg" fullWidth={true} PaperComponent={DialogPaper}>
        <DialogTitle style={dialogTitle}>{fm('cognition.cdr.cdr')}</DialogTitle>
        <DialogContent ref={dialogContentRef} style={{ ...dialogContent, overflowX: 'hidden' }}>
          <CDRForm
            formData={{
              onChange: (values: TOnChangeValues) => {
                setEditingData({ ...editingData, ...values });
              },
              document: editingData as ICDR,
            }}
            viewing={false}
            parent={dialogContentRef.current}
          />
        </DialogContent>
        <DialogActions style={dialogActions}>
          <StyledButton style={{ marginRight: '2rem' }} onClick={() => setDialogOpen(false)}>
            {fm('general.cancel')}
          </StyledButton>
          <ActionButton
            text="general.save"
            width={10}
            height={3}
            fontSize={16}
            onClick={() => {
              addNewCdr();
              setDialogOpen(false);
            }}
          />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

interface IOwnProps extends IFormContext<IAlzheimer> {}

export default CDRDialog;
